import { useEffect, useRef, useState } from 'react';

const values = [
  {
    icon: 'ri-code-s-slash-line',
    label: 'CUSTOM SOFTWARE',
    desc: 'Our projects involve experienced project managers, developers, UI/UX designers, marketers, and QA engineers.',
    color: '#8EC9BB',
    bg: 'rgba(142,201,187,0.08)',
  },
  {
    icon: 'ri-customer-service-2-line',
    label: 'IT CONSULTING',
    desc: 'We also provide IT consulting, helping businesses make informed technology decisions, optimize processes, and implement modern tools.',
    color: '#F2CF59',
    bg: 'rgba(242,207,89,0.08)',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    label: 'MARKETING & ADS',
    desc: 'A separate direction is advertising and marketing support: we develop promotion strategies, strengthen brand presence, and help products find their audience.',
    color: '#FA6E4F',
    bg: 'rgba(250,110,79,0.08)',
  },
];

export default function AboutValues() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="w-full" style={{ background: '#0d0d0d' }}>
      <div className="px-10 lg:px-20 py-24">
        {/* Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <p className="text-[#8EC9BB]/50 text-xs tracking-[0.5em] uppercase mb-4">What drives us</p>
            <h2 className="boldonse text-white leading-none" style={{ fontSize: 'clamp(44px, 6vw, 90px)' }}>
              THREE<br />
              <span style={{ color: '#8EC9BB' }}>PILLARS</span>
            </h2>
          </div>
          <p className="text-white/40 text-base leading-relaxed max-w-xs lg:text-right">
            We value speed, transparency and efficiency — you always understand how your project is progressing and what results it delivers.
          </p>
        </div>

        {/* Values */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px" style={{ background: '#222' }}>
          {values.map((v, i) => (
            <div
              key={v.label}
              className="p-10 group cursor-default"
              style={{
                background: '#0d0d0d',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                transition: `all 0.8s ease ${i * 120}ms`,
              }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-8 transition-transform duration-300 group-hover:scale-110"
                style={{ background: v.bg }}
              >
                <i className={`${v.icon} text-2xl`} style={{ color: v.color }} />
              </div>
              <p className="boldonse mb-4" style={{ color: v.color, fontSize: '13px', letterSpacing: '0.3em' }}>
                {v.label}
              </p>
              <p className="text-white/50 text-base leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>

        {/* Big stat strip */}
        <div
          className={`mt-px grid grid-cols-3 gap-px transition-all duration-1000 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ background: '#222' }}
        >
          {[
            { num: '50+', label: 'Projects delivered worldwide' },
            { num: '24h', label: 'Average response time' },
            { num: '100%', label: 'Commitment to transparency' },
          ].map((s) => (
            <div key={s.label} className="px-10 py-10" style={{ background: '#0d0d0d' }}>
              <p className="boldonse text-white mb-2" style={{ fontSize: 'clamp(40px, 5vw, 72px)', lineHeight: 1 }}>
                {s.num}
              </p>
              <p className="text-white/35 text-sm tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}